import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle, 
  RefreshCw, 
  Search, 
  Clock,
  CircleDollarSign,
  AlertTriangle,
  XCircle,
  Filter,
  Inbox,
  Play,
  CheckCircle2,
  Calendar
} from 'lucide-react';
import { 
  useLazyGetAllServiceQuery, 
  useLazyGetBookingsQuery, 
  useUpdateBookingStatusMutation 
} from '../../../redux/actions/Admin/ServiceAdmin';
import { IserviceResponse } from '../../../types/Response/AdminResponse';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = ["All Status", "Pending", "Confirmed", "InProgress", "Completed", "Cancelled"];
const PET_TYPE_OPTIONS = ["All Pets", "Dog", "Cat", "Bird"];

// Helper function to extract start and end dates of the current month (YYYY-MM-DD)
const getCurrentMonthDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  
  return {
    start: `${year}-${month}-01`,
    end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
  };
};

// UI Badge Context Stylings based on Enum Status values
const getStatusBadgeStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case "confirmed":
    case "approved":
      return "bg-green-50 text-green-700 border-green-200";
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "inprogress":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "cancelled":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "completed":
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

// Exact Mapping helper bound tightly to your explicit backend Enum layout values
const mapStatusToNumber = (status: string): number => {
  switch (status.toLowerCase()) {
    case 'pending': return 1;
    case 'confirmed': return 2;
    case 'inprogress': return 3;
    case 'completed': return 4;
    case 'cancelled': return 5;
    default: return 1;
  }
};

export default function ManageBookings() {
  const navigate = useNavigate()
  // 1. Fetch service list via lazy hook mapping structure
  const [triggerGetAllServices, { data: services = [], isLoading: servicesLoading, isError: servicesError }] = useLazyGetAllServiceQuery();
  
  // 2. Lazy Trigger Hook
  const [triggerGetBookings, { data: bookingsResponse, isLoading: bookingsLoading, isError: bookingsError }] = useLazyGetBookingsQuery();
  const [updateBookingStatus, { isLoading: isStatusMutating }] = useUpdateBookingStatusMutation();

  const [view, setView] = useState<'services-select' | 'dashboard-workspace'>('services-select');
  const [selectedService, setSelectedService] = useState<IserviceResponse | null>(null);

  const defaultDates = getCurrentMonthDateRange();
  const [filters, setFilters] = useState({
    startDate: defaultDates.start,
    endDate: defaultDates.end,
    status: 'All Status',
    petType: 'All Pets',
    search: ''
  });

  // Fetch initial system services sectors layout grid on mounting sequence loops
  useEffect(() => {
    triggerGetAllServices();
  }, [triggerGetAllServices]);

  // Extract array listings out of backend custom "bookingData" key safely
  const bookingsList = bookingsResponse && (bookingsResponse as any).bookingData 
    ? (bookingsResponse as any).bookingData 
    : Array.isArray(bookingsResponse) ? bookingsResponse : [];

  const fetchBackendData = () => {
    if (!selectedService) return;

    const apiPayload: any = {
      serviceId: selectedService.id,
      startDate: filters.startDate,
      endDate: filters.endDate
    };

    if (filters.status !== "All Status") apiPayload.status = filters.status;
    if (filters.petType !== "All Pets") apiPayload.petType = filters.petType.toLowerCase();
    if (filters.search.trim()) apiPayload.search = filters.search.trim();

    triggerGetBookings(apiPayload);
  };

  // Re-fetch automatically on filter change constraints shift triggers
  useEffect(() => {
    if (selectedService) {
      fetchBackendData();
    }
  }, [selectedService, filters.status, filters.petType, filters.startDate, filters.endDate]);

  const handleSelectService = (service: IserviceResponse) => {
    const currentMonth = getCurrentMonthDateRange();
    setFilters({
      startDate: currentMonth.start,
      endDate: currentMonth.end,
      status: "All Status",
      petType: "All Pets",
      search: ""
    });
    setSelectedService(service);
    setView('dashboard-workspace');

    triggerGetBookings({
      serviceId: service.id,
      startDate: currentMonth.start,
      endDate: currentMonth.end
    });
  };

  const handleBackToServices = () => {
    setSelectedService(null);
    setView('services-select');
  };

  const handleAlterBookingStatus = async (bookingId: number, statusString: string) => {
    const statusNumber = mapStatusToNumber(statusString);
    try {
      await updateBookingStatus({ bookingId, status: statusNumber, paymentStatus:'',notifyCustomer:true,paymentMethod:'',remarks:'' }).unwrap();
      fetchBackendData(); // Refreshes workspace dataset
    } catch (err) {
      console.error("Booking status mutation workflow failed:", err);
    }
  };

  // Global metric aggregations mapping your response structures
  const totalAppointmentsCount = bookingsList.length;
  const pendingCount = bookingsList.filter((b: any) => b.status?.toLowerCase() === 'pending').length;
  const cancelledCount = bookingsList.filter((b: any) => b.status?.toLowerCase() === 'cancelled').length;
  const computedRevenueTotal = bookingsList.reduce((sum: number, b: any) => sum + (b.finalPrice || 0), 0);

  const sortedServices = [...services].sort((a, b) => a.name.localeCompare(b.name));
const handleCreateBooking = () =>{
  navigate('/admin/services/ManageBookings/CreateBookingScreen')
}
  return (
    <div className="min-h-screen px-4 py-6 md:p-8">
      
      {/* =========================================================
          VIEW 1: SELECT SERVICE SCREEN (Organic Asymmetric Pebbles Grid)
          ========================================================= */}
      {view === 'services-select' && (
        <div className="max-w-[1400px] mx-auto">
          <div className="border-b border-gray-300/40 pb-6 mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-logoHeading">Bookings Station</h1>
            <p className="text-gray-400 mt-1 text-xs md:text-sm font-medium">
              Select a service segment below to manage operational lifecycle queues.
            </p>
          </div>
          {/* <button 
                onClick={() => handleCreateBooking()} 
                className="mt-4 bg-logoHeading text-white px-5 py-2.5 rounded-xl text-xs font-semibold cursor-pointer hover:opacity-90 transition"
              >
                create booking
              </button> */}

          {servicesLoading ? (
            <div className="flex flex-col items-center justify-center h-[250px]">
              <Loader2 className="w-8 h-8 md:w-10 md:h-10 animate-spin text-mainBrandColor" />
              <p className="mt-3 text-xs md:text-sm font-semibold text-gray-400 uppercase tracking-wider">Syncing Channels...</p>
            </div>
          ) : servicesError ? (
            <div className="bg-white rounded-2xl p-6 md:p-8 text-center border border-mediumgray max-w-md mx-auto shadow-sm">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h2 className="mt-4 text-base font-bold text-gray-800">Connection Failed</h2>
              <button 
                onClick={() => triggerGetAllServices()} 
                className="mt-4 bg-logoHeading text-white px-5 py-2.5 rounded-xl text-xs font-semibold cursor-pointer hover:opacity-90 transition"
              >
                Retry Registry Loading
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-x-8 md:gap-y-12 justify-center">
              {sortedServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleSelectService(service)}
                  className="group flex flex-col items-center cursor-pointer transition-all duration-300 relative bg-white p-3 md:p-0 rounded-2xl md:bg-transparent border border-gray-100 md:border-0 shadow-xs md:shadow-none"
                >
                  <div className="relative size-[180px] mx-auto">
                    <div className={`w-full h-full overflow-hidden border-[2px] md:border-[3px] transition-all duration-500 bg-gray-50 
                      ${service.isActive 
                        ? 'border-mainBrandColor rounded-[48%_52%_68%_32%_/_42%_48%_52%_58%] group-hover:rounded-[52%_48%_35%_65%_/_48%_55%_45%_52%] group-hover:shadow-md' 
                        : 'border-gray-200 rounded-[50%] opacity-60'
                      }`}
                    >
                      <img
                        src={service.logoUrl || 'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400'}
                        alt={service.name}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${!service.isActive ? 'grayscale' : ''}`}
                      />
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 text-center space-y-0.5 md:space-y-1 w-full px-1">
                    <h3 className="text-sm md:text-lg font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors duration-200 line-clamp-1">
                      {service.name}
                    </h3>
                    <span className="text-[9px] md:text-[10px] font-extrabold uppercase tracking-widest text-gray-400 group-hover:text-logoHeading transition-colors block">
                      Inspect Desk &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =========================================================
          VIEW 2: RECONFIGURED WORKSPACE VIEW (MOBILE SENSITIVE)
          ========================================================= */}
      {view === 'dashboard-workspace' && selectedService && (
        <div className="max-w-[1400px] mx-auto space-y-6">
          
          {/* Header Back Link */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-300/40 pb-5">
            <div className="space-y-1">
              <button
                onClick={handleBackToServices}
                className="inline-flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-logoHeading uppercase tracking-wider transition cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
                Switch Modules Selection
              </button>
              <h1 className="text-xl md:text-2xl font-black text-logoHeading tracking-tight">
                {selectedService.name} <span className="font-semibold text-gray-400">/ Appointments Hub</span>
              </h1>
            </div>
          </div>

          {/* CHIPS ANALYTICS PANELS GRID */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-2xl p-3 md:p-5 shadow-xs border border-mediumgray flex items-center justify-between">
              <div className="space-y-0.5 md:space-y-1">
                <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wide block">Total Charted</span>
                <h2 className="text-lg md:text-2xl font-black text-logoHeading">{totalAppointmentsCount}</h2>
              </div>
              <div className="p-2 bg-lightgray border border-mediumgray hidden sm:block rounded-xl">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-logoHeading" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-3 md:p-5 shadow-xs border border-mediumgray flex items-center justify-between">
              <div className="space-y-0.5 md:space-y-1">
                <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wide block">Staged Pending</span>
                <h2 className="text-lg md:text-2xl font-black text-amber-600">{pendingCount}</h2>
              </div>
              <div className="p-2 bg-amber-50 border border-amber-100 hidden sm:block rounded-xl">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-3 md:p-5 shadow-xs border border-mediumgray flex items-center justify-between">
              <div className="space-y-0.5 md:space-y-1">
                <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wide block">Void / Cancelled</span>
                <h2 className="text-lg md:text-2xl font-black text-rose-600">{cancelledCount}</h2>
              </div>
              <div className="p-2 bg-rose-50 border border-rose-100 hidden sm:block rounded-xl">
                <XCircle className="w-4 h-4 md:w-5 md:h-5 text-rose-500" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-3 md:p-5 shadow-xs border border-mediumgray flex items-center justify-between">
              <div className="space-y-0.5 md:space-y-1">
                <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wide block">Aggregated Value</span>
                <h2 className="text-lg md:text-2xl font-black text-mainBrandColor">AED {computedRevenueTotal.toLocaleString()}</h2>
              </div>
              <div className="p-2 bg-green-50 border border-green-100 hidden sm:block rounded-xl">
                <CircleDollarSign className="w-4 h-4 md:w-5 md:h-5 text-mainBrandColor" />
              </div>
            </div>
          </div>

          {/* ADVANCED CRITERIA FILTER BAR PANEL PANEL BOX */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-mediumgray space-y-3">
            <div className="flex flex-col lg:flex-row items-center gap-3">
              
              <div className="relative w-full lg:flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input 
                  type="text"
                  placeholder="Search by pet name, breed or booking ID..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && fetchBackendData()} 
                  className="w-full pl-10 pr-4 py-2.5 border border-mediumgray bg-lightgray rounded-xl text-xs font-semibold focus:border-mainBrandColor focus:bg-white outline-none transition"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full lg:w-auto shrink-0">
                <div>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-2.5 py-2.5 text-xs font-bold outline-none text-gray-700 cursor-pointer focus:border-mainBrandColor focus:bg-white"
                  >
                    {STATUS_OPTIONS.map((st, i) => <option key={i} value={st}>{st}</option>)}
                  </select>
                </div>

                <div>
                  <select
                    value={filters.petType}
                    onChange={(e) => setFilters({ ...filters, petType: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-2.5 py-2.5 text-xs font-bold outline-none text-gray-700 cursor-pointer focus:border-mainBrandColor focus:bg-white"
                  >
                    {PET_TYPE_OPTIONS.map((pt, i) => <option key={i} value={pt}>{pt}</option>)}
                  </select>
                </div>

                <div>
                  <input 
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-2 py-2 text-[11px] font-bold text-gray-700 outline-none focus:border-mainBrandColor focus:bg-white"
                  />
                </div>

                <div>
                  <input 
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-2 py-2 text-[11px] font-bold text-gray-700 outline-none focus:border-mainBrandColor focus:bg-white"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={fetchBackendData}
                className="w-full lg:w-auto bg-logoHeading hover:opacity-90 text-white px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider font-extrabold shadow-2xs cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
              >
                <Filter className="w-3.5 h-3.5" /> Filter
              </button>

            </div>
          </div>

          {/* DATA STAGE COMPONENT INVOCATION BOUNDARY */}
          {bookingsLoading ? (
            <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-mediumgray shadow-2xs">
              <Loader2 className="w-8 h-8 text-mainBrandColor animate-spin" />
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-2.5">Fetching Filtered Data Streams...</p>
            </div>
          ) : bookingsError ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-red-100 max-w-md mx-auto shadow-2xs">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
              <p className="text-sm font-bold text-gray-800 mt-2">Error connecting to query parameter api filter processors endpoint nodes.</p>
            </div>
          ) : (
            
            <div>
              {/* DESKTOP VIEWPORTS: Desktop Table Component Display Layout Boundary */}
              <div className="hidden md:block bg-white rounded-2xl shadow-xs border border-mediumgray overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-lightgray border-b border-mediumgray text-xs font-bold uppercase tracking-wider text-gray-400">
                        <th className="py-4 px-5">Booking ID</th>
                        <th className="py-4 px-5">Pet Details</th>
                        <th className="py-4 px-5">Package Tier</th>
                        <th className="py-4 px-5">Schedule Time</th>
                        <th className="py-4 px-5">Status</th>
                        <th className="py-4 px-5">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm font-semibold">
                      {bookingsList.length > 0 ? (
                        bookingsList.map((item: any) => {
                          const statusLabel = item.status || "Pending";
                          const normalizedStatus = statusLabel.toLowerCase();
                          
                          return (
                            <tr key={item.bookingId} className="hover:bg-lightgray/50 transition duration-150">
                              <td className="py-4 px-5">
                                <span className="font-mono text-logoHeading text-xs font-bold block">#{item.bookingId}</span>
                              </td>
                              <td className="py-4 px-5">
                                <div className="space-y-0.5">
                                  <p className="text-slate-800 font-bold">{item.petName}</p>
                                  <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">
                                    {item.petType} <span className="font-medium text-gray-300">/</span> {item.petBreed} ({item.petSize})
                                  </p>
                                </div>
                              </td>
                              <td className="py-4 px-5">
                                <div className="space-y-0.5">
                                  <p className="text-iconColor font-bold">{item.packageName}</p>
                                  <p className="text-[11px] text-logoHeading font-bold">AED {item.finalPrice}</p>
                                </div>
                              </td>
                              <td className="py-4 px-5">
                                <div className="space-y-0.5">
                                  <p className="text-slate-800 font-bold">{item.bookingDate}</p>
                                  <p className="text-[11px] text-gray-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {item.startTime} - {item.endTime}
                                  </p>
                                </div>
                              </td>
                              <td className="py-4 px-5">
                                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadgeStyle(statusLabel)}">
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="py-4 px-5 text-xs font-medium text-gray-400 max-w-[150px] truncate">
                                {item.notes || "—"}
                              </td>
                              
                              {/* <td className="py-4 px-5 text-right">
                                <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                                  {normalizedStatus === 'pending' && (
                                    <button onClick={() => handleAlterBookingStatus(item.bookingId, 'confirmed')} disabled={isStatusMutating} className="bg-mainBrandColor hover:opacity-90 text-white px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider transition cursor-pointer">
                                      Confirm
                                    </button>
                                  )}
                                  {normalizedStatus === 'confirmed' && (
                                    <button onClick={() => handleAlterBookingStatus(item.bookingId, 'inprogress')} disabled={isStatusMutating} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider transition cursor-pointer inline-flex items-center gap-1">
                                      <Play className="w-3 h-3 fill-white" /> Start Job
                                    </button>
                                  )}
                                  {normalizedStatus !== 'completed' && normalizedStatus !== 'cancelled' && (
                                    <>
                                      {(normalizedStatus === 'confirmed' || normalizedStatus === 'inprogress') && (
                                        <button onClick={() => handleAlterBookingStatus(item.bookingId, 'completed')} disabled={isStatusMutating} className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider transition cursor-pointer">
                                          Complete
                                        </button>
                                      )}
                                      <button onClick={() => handleAlterBookingStatus(item.bookingId, 'cancelled')} disabled={isStatusMutating} className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider transition cursor-pointer">
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td> */}
                            </tr>
                          );
                        })
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* MOBILE VIEWPORTS: Renders as clear vertical structural administration parameter cards blocks */}
              <div className="md:hidden space-y-4">
                {bookingsList.length > 0 ? (
                  bookingsList.map((item: any) => {
                    const statusLabel = item.status || "Pending";
                    const normalizedStatus = statusLabel.toLowerCase();

                    return (
                      <div key={item.bookingId} className="bg-white rounded-2xl border border-mediumgray p-4 shadow-xs space-y-4">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                          <span className="font-mono text-logoHeading text-xs font-bold">#{item.bookingId}</span>
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusBadgeStyle(statusLabel)}`}>
                            {statusLabel}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-x-2 gap-y-3 text-xs">
                          <div>
                            <span className="text-[10px] text-gray-400 block uppercase font-bold tracking-wider">Pet Identity</span>
                            <p className="font-bold text-slate-800 mt-0.5">{item.petName}</p>
                            <p className="text-[10px] text-gray-400 font-medium">{item.petBreed} ({item.petSize})</p>
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-400 block uppercase font-bold tracking-wider">Package Plan</span>
                            <p className="font-bold text-iconColor mt-0.5">{item.packageName}</p>
                            <p className="text-[10px] text-logoHeading font-bold">AED {item.finalPrice}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-[10px] text-gray-400 block uppercase font-bold tracking-wider">Timestamps Window</span>
                            <p className="font-bold text-slate-700 mt-0.5 flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" /> {item.bookingDate} 
                              <span className="text-gray-300 font-medium px-0.5">|</span>
                              <Clock className="w-3.5 h-3.5 text-gray-400" /> {item.startTime} - {item.endTime}
                            </p>
                          </div>
                          {item.notes && (
                            <div className="col-span-2 bg-lightgray rounded-xl p-2 border border-gray-100">
                              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Notes</span>
                              <p className="text-gray-500 font-medium mt-0.5">{item.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2 items-center justify-end">
                          {normalizedStatus === 'pending' && (
                            <button onClick={() => handleAlterBookingStatus(item.bookingId, 'confirmed')} disabled={isStatusMutating} className="bg-mainBrandColor text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider w-full sm:w-auto cursor-pointer">
                              Confirm
                            </button>
                          )}
                          {normalizedStatus === 'confirmed' && (
                            <button onClick={() => handleAlterBookingStatus(item.bookingId, 'inprogress')} disabled={isStatusMutating} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider w-full sm:w-auto flex items-center justify-center gap-1 cursor-pointer">
                              <Play className="w-3 h-3 fill-white" /> Start Job
                            </button>
                          )}
                          {normalizedStatus !== 'completed' && normalizedStatus !== 'cancelled' && (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              {(normalizedStatus === 'confirmed' || normalizedStatus === 'inprogress') && (
                                <button onClick={() => handleAlterBookingStatus(item.bookingId, 'completed')} disabled={isStatusMutating} className="bg-blue-600 text-white px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex-1 sm:flex-initial text-center cursor-pointer">
                                  Complete
                                </button>
                              )}
                              <button onClick={() => handleAlterBookingStatus(item.bookingId, 'cancelled')} disabled={isStatusMutating} className="bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex-1 sm:flex-initial text-center cursor-pointer">
                                Cancel
                              </button>
                            </div>
                          )}
                        </div> */}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-20 text-gray-400 bg-white rounded-2xl border border-dashed border-mediumgray font-medium text-sm">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <Inbox className="w-8 h-8 text-gray-300" />
                      <p>No active dashboard bookings retrieved from current filters.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
}